/**
 * @file Admin users listing page.
 *
 * Presents a paginated table of platform users with global roles, verification status,
 * and tenant memberships. Filters are driven by URL search params (client, company,
 * role, name/email search) so views are bookmarkable and survive refresh.
 *
 * @see {@link useUsersWithTenantsQuery} — `GET /auth/api/v1/auth/users/tenants`
 * @see {@link useAllTenantsWithCompanies} — populates client/company filter dropdowns
 *
 * URL search params:
 * - `page`, `pageSize` — pagination
 * - `search` — case-insensitive name or email filter (committed after debounce or Enter)
 * - `clientId` — limit to users with membership on that tenant (client)
 * - `companyId` — limit to users with membership on that company (sub-tenant)
 * - `role` — global user role (`ADMIN`, `DEVELOPER`, `USER`, `CUSTOMER`)
 */
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import PageTransition from '@/components/ui/PageTransition'
import { useUsersWithTenantsQuery } from '@/api/auth/queries'
import type { UserWithTenantMemberships } from '@/api/auth/interfaces'
import {
  Card,
  Table,
  Tag,
  Typography,
  Avatar,
  Space,
  Button,
  Select,
  Form,
  Input,
  Empty,
  Flex,
  Tooltip,
  theme
} from 'antd'
import { TeamOutlined, EyeOutlined, ClearOutlined, PlusOutlined } from '@ant-design/icons'
import { Link } from '@tanstack/react-router'
import { UserRoleEditor } from '@/components/tenant/UserRoleEditor'
import { PageHeader } from '@/components/ui/PageHeader'
import { FilterBar } from '@/components/ui/FilterBar'
import { UserEmailVerificationActions, isUserEmailVerified } from '@/components/admin/UserEmailVerificationActions'
import { CreateUserModal } from '@/components/admin/CreateUserModal'
import { TenantRoleTag } from '@/components/tenant'
import { useAuth } from '@/hooks/useAuth'
import { useAllTenantsWithCompanies } from '@/hooks/useAllTenantsWithCompanies'
import { useUrlSearchState } from '@/hooks/useUrlSearchState'
import { canEditGlobalUserRole } from '@/utils/authz'
import { getUserDisplayName, getUserInitial } from '@/utils/userDisplay'
import { formatDate } from '@/utils/dateFormat';

const { Text } = Typography

type UsersSearch = {
  page: number
  pageSize: number
  search?: string
  clientId?: string
  companyId?: string
  role?: string
}

/** Builds route search, omitting empty optional fields. */
const buildUsersSearch = ({
  page,
  pageSize,
  search,
  clientId,
  companyId,
  role
}: UsersSearch): UsersSearch => ({
  page,
  pageSize,
  ...(search ? { search } : {}),
  ...(clientId ? { clientId } : {}),
  ...(companyId ? { companyId } : {}),
  ...(role ? { role } : {})
})

/** Global role options for the filter dropdown (matches ai-auth `UserRole`). */
const USER_ROLES = ['ADMIN', 'DEVELOPER', 'USER', 'CUSTOMER'] as const

/** Route definition: validates and normalizes `/admin/users/` search params. */
export const Route = createFileRoute('/admin/users/')({
  validateSearch: (search) =>
    z
      .object({
        page: z.coerce.number().int().min(1).catch(1),
        pageSize: z.coerce.number().int().min(1).max(100).catch(10),
        search: z.string().optional().catch(undefined),
        clientId: z.string().optional().catch(undefined),
        companyId: z.string().optional().catch(undefined),
        role: z.string().optional().catch(undefined)
      })
      .parse(search),
  component: UsersPage,
})

/**
 * Admin users list: search by name/email, filter by tenant, company, or global role; paginate.
 *
 * **URL vs input:** `search` in the address bar is the committed filter. The search field
 * keeps a local `searchDraft` so typing feels instant; the draft is debounced into the URL.
 * Dropdown filters update the URL immediately (no debounce) and reset to page 1.
 */
function UsersPage() {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const { payload } = useAuth()
  const canEditRoles = canEditGlobalUserRole(payload?.role)
  const navigate = Route.useNavigate()
  const { page, pageSize, search, clientId, companyId, role } = Route.useSearch()

  const [form] = Form.useForm()

  const committedSearch = search ?? ''
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false)

  /** Writes the name/email filter to the URL and resets to page 1. */
  const applySearchToUrl = useCallback(
    (value: string) => {
      const trimmed = value.trim()
      navigate({
        to: '/admin/users/',
        replace: true,
        search: buildUsersSearch({
          page: 1,
          pageSize,
          search: trimmed || undefined,
          clientId,
          companyId,
          role
        })
      } as any)
    },
    [navigate, pageSize, clientId, companyId, role]
  )

  const { searchDraft, setSearchDraft, changeDraft, searchNow, isSearchPending, isFiltering } =
    useUrlSearchState(committedSearch, applySearchToUrl)

  const { tenants: clients } = useAllTenantsWithCompanies()

  const allCompanies = useMemo(() => {
    const companies: Array<{ id: string; name: string; clientId: string; clientName: string }> = []
    clients.forEach(client => {
      client.companies?.forEach(company => {
        companies.push({
          id: company.id,
          name: company.name,
          clientId: client.id,
          clientName: client.name
        })
      })
    })
    return companies
  }, [clients])

  const filteredCompanies = useMemo(() => {
    if (!clientId) return allCompanies
    return allCompanies.filter(c => c.clientId === clientId)
  }, [allCompanies, clientId])

  const { data, isLoading, isFetching } = useUsersWithTenantsQuery(
    page,
    pageSize,
    companyId,
    clientId,
    role,
    committedSearch || undefined
  )

  const users: UserWithTenantMemberships[] = useMemo(
    () => data?.users ?? [],
    [data]
  )

  const total = data?.pagination?.total ?? users.length

  const hasDropdownFilters = Boolean(clientId || companyId || role)
  const hasAnyFilter = hasDropdownFilters || isFiltering

  const handleFilterChange = useCallback((field: string, value: string | undefined) => {
    const nextClientId = field === 'clientId' ? (value || undefined) : clientId
    let nextCompanyId = field === 'companyId' ? (value || undefined) : companyId

    if (field === 'clientId' && !value) {
      nextCompanyId = undefined
      form.setFieldValue('companyId', undefined)
    } else if (field === 'clientId' && value) {
      const selectedCompany = allCompanies.find(c => c.id === companyId)
      if (selectedCompany && selectedCompany.clientId !== value) {
        nextCompanyId = undefined
        form.setFieldValue('companyId', undefined)
      }
    }

    navigate({
      to: '/admin/users/',
      search: buildUsersSearch({
        page: 1,
        pageSize,
        search: committedSearch || undefined,
        clientId: nextClientId,
        companyId: nextCompanyId,
        role: field === 'role' ? (value || undefined) : role
      })
    } as any)
  }, [navigate, form, companyId, allCompanies, pageSize, clientId, role, committedSearch])

  const handleClearFilters = useCallback(() => {
    setSearchDraft('')
    form.resetFields()
    navigate({
      to: '/admin/users/',
      search: { page: 1, pageSize } as any
    })
  }, [form, navigate, pageSize, setSearchDraft])

  useEffect(() => {
    form.setFieldsValue({
      clientId,
      companyId,
      role
    })
  }, [form, clientId, companyId, role])

  const tableLoading = isLoading || (isFetching && (isSearchPending || isFiltering))

  const columns = [
    {
      title: t('admin:users.columns.user'),
      key: 'user',
      render: (user: UserWithTenantMemberships) => (
        <Flex align="center" gap={token.marginSM} style={{ minWidth: 0 }}>
          <Avatar size="large" src={user.picture || undefined} style={{ backgroundColor: token.colorPrimary, flexShrink: 0 }}>
            {!user.picture ? getUserInitial(user) : null}
          </Avatar>
          <Flex vertical style={{ minWidth: 0, maxWidth: 280 }}>
            <Text strong ellipsis={{ tooltip: true }}>{user.name || '—'}</Text>
            <Text type="secondary" ellipsis={{ tooltip: true }} style={{ fontSize: token.fontSizeSM }}>{user.email}</Text>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM, color: token.colorTextTertiary }}>
              {t('admin:users.joined', { date: formatDate(user.createdAt) })}
            </Text>
          </Flex>
        </Flex>
      )
    },
    {
      title: t('admin:users.detail.globalRole'),
      key: 'role',
      width: 200,
      render: (user: UserWithTenantMemberships) => (
        <UserRoleEditor
          userId={user.id}
          currentRole={user.role}
          currentUserId={payload?.userId}
          userName={getUserDisplayName(user)}
          canEdit={canEditRoles}
        />
      )
    },
    {
      title: t('admin:users.columns.tenants'),
      key: 'tenants',
      render: (user: UserWithTenantMemberships) => (
        <Space wrap>
          {user.clientMemberships?.length ? (
            user.clientMemberships.slice(0, 3).map(cm => (
              <Link key={cm.id} to={`/admin/tenants/${cm.clientId}`}>
                <TenantRoleTag role={cm.role} tenantName={cm.client?.name || cm.clientId} style={{ marginRight: 0 }} />
              </Link>
            ))
          ) : (
            <Text type="secondary">{t('admin:users.none')}</Text>
          )}
          {user.clientMemberships?.length > 3 ? (
            <Tag>{t('admin:users.more', { count: user.clientMemberships.length - 3 })}</Tag>
          ) : null}
        </Space>
      )
    },
    {
      title: t('admin:users.columns.companies'),
      key: 'companies',
      render: (user: UserWithTenantMemberships) => (
        <Text>{user.companyMemberships?.length ?? 0}</Text>
      )
    },
    {
      title: t('admin:users.columns.status'),
      key: 'status',
      width: 280,
      render: (user: UserWithTenantMemberships) => {
        const emailVerified = isUserEmailVerified(user.emailVerified);
        return (
          <Space orientation="vertical" size={8} style={{ width: '100%' }}>
            <Space wrap>
              <Tag color={emailVerified ? 'green' : 'orange'}>
                {emailVerified ? t('admin:users.emailVerified') : t('admin:users.emailUnverified')}
              </Tag>
              {user.phoneNumber ? (
                <Tag color={user.phoneVerified ? 'green' : 'default'}>
                  {user.phoneVerified ? t('admin:users.phoneVerified') : t('admin:users.phoneUnverified')}
                </Tag>
              ) : null}
            </Space>
            {!emailVerified && user.email ? (
              <UserEmailVerificationActions
                userId={user.id}
                email={user.email}
                emailVerified={user.emailVerified}
                userName={getUserDisplayName(user)}
                variant="table"
              />
            ) : null}
          </Space>
        );
      }
    },
    {
      title: t('admin:users.columns.actions'),
      key: 'actions',
      align: 'center' as const,
      width: 72,
      render: (user: UserWithTenantMemberships) => (
        <Tooltip title={t('admin:users.view')}>
          <Link to={`/admin/users/${user.id}`}>
            <Button size="small" icon={<EyeOutlined />} aria-label={t('admin:users.view')} />
          </Link>
        </Tooltip>
      )
    }
  ]

  return (
    <PageTransition animation="fadeInUp">
      <PageHeader
        icon={<TeamOutlined />}
        title={t('admin:users.title')}
        description={t('admin:users.subtitle')}
        actions={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateUserModalOpen(true)}>
            {t('admin:users.create.button')}
          </Button>
        }
      />

      <Form
        form={form}
        component={false}
        onValuesChange={(changedValues) => {
          Object.keys(changedValues).forEach(key => {
            handleFilterChange(key, changedValues[key])
          })
        }}
      >
        <FilterBar
          actions={
            hasAnyFilter ? (
              <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
                {t('admin:users.clearFilters')}
              </Button>
            ) : undefined
          }
        >
          <Input.Search
            allowClear
            placeholder={t('admin:users.searchByNameOrEmail')}
            aria-label={t('admin:users.search')}
            value={searchDraft}
            loading={isFetching && isSearchPending}
            style={{ flex: '1 1 220px', minWidth: 160 }}
            onChange={(e) => changeDraft(e.target.value)}
            onSearch={searchNow}
          />

          <Form.Item name="clientId" noStyle>
            <Select
              placeholder={t('admin:users.allClients')}
              aria-label={t('admin:users.client')}
              allowClear
              showSearch
              style={{ flex: '1 1 180px', minWidth: 160 }}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={clients.map(client => ({
                value: client.id,
                label: client.name
              }))}
            />
          </Form.Item>

          <Form.Item name="companyId" noStyle>
            <Select
              placeholder={t('admin:users.allCompanies')}
              aria-label={t('admin:users.company')}
              allowClear
              showSearch
              style={{ flex: '1 1 200px', minWidth: 160 }}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={filteredCompanies.map(company => ({
                value: company.id,
                label: `${company.name} (${company.clientName})`
              }))}
            />
          </Form.Item>

          <Form.Item name="role" noStyle>
            <Select
              placeholder={t('admin:users.allRoles')}
              aria-label={t('admin:users.role')}
              allowClear
              style={{ flex: '1 1 140px', minWidth: 140 }}
              options={USER_ROLES.map(value => ({ value, label: t(`admin:users.roleOptions.${value}`) }))}
            />
          </Form.Item>

          {(isFiltering || isSearchPending) && (
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {isSearchPending
                ? t('admin:users.searchPending')
                : t('admin:users.searchResults', { count: total, query: committedSearch })}
            </Text>
          )}
        </FilterBar>
      </Form>

      <Card
        styles={{ body: { padding: 0 } }}
      >
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={tableLoading}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  isFiltering && !isSearchPending
                    ? t('admin:users.noSearchResults', { query: committedSearch })
                    : t('admin:users.empty')
                }
              />
            )
          }}
          pagination={{
            style: { paddingInline: token.padding },
            current: page,
            pageSize,
            total,
            showTotal: (total: number, range: [number, number]) =>
              t('admin:users.paginationTotal', { start: range[0], end: range[1], total }),
            onChange: (p, ps) => {
              navigate({
                to: '/admin/users/',
                search: buildUsersSearch({
                  page: p,
                  pageSize: ps,
                  search: committedSearch || undefined,
                  clientId,
                  companyId,
                  role
                })
              } as any)
            }
          }}
        />
      </Card>

      <CreateUserModal
        open={createUserModalOpen}
        onClose={() => setCreateUserModalOpen(false)}
      />
    </PageTransition>
  )
}
